/**
 * QL-33 / QL-261 — standalone mode of the ADVISOR flow.
 *
 * A partner system opens `/clients/:clientId/flows/:instanceId?standalone=true`
 * (the `launchUrl` the public API returns) and the web app hides its own
 * chrome. Standalone is a property of the LINK: nothing is persisted, so the
 * rule that keeps the mode alive is simply that the query param must never
 * disappear while the user is inside the flow. Every in-flow URL write goes
 * through {@link mergeStandaloneParams} so it merges with — rather than
 * overwrites — whatever other query state the target path carries.
 *
 * The optional `internalRedirectUrl` param is the legacy, link-carried return
 * target; the value stored on the instance (set over the public API) wins.
 *
 * QL-262 — the second rule is that there is NO WAY OUT of the flow while in
 * standalone mode: {@link classifyStandaloneNavigation} sorts every in-app
 * navigation target into in-flow (allowed, with the params re-added when a
 * target dropped them), logout (the sign-out/sign-in round trip, allowed),
 * external (another origin, allowed) or out-of-flow (cancelled by the web
 * app's navigation guard, which sends the user to `internalRedirectUrl`
 * instead when one exists).
 */

export const STANDALONE_PARAM = "standalone";
export const INTERNAL_REDIRECT_URL_PARAM = "internalRedirectUrl";

/** The advisor flow-instance routes: the wizard, `/process`, and anything nested under them. */
const FLOW_INSTANCE_PATH = /^\/clients\/[^/]+\/flows\/[^/]+(\/[^?#]*)?$/;

/** True for any route under `/clients/:clientId/flows/:instanceId`. */
export function isFlowInstancePath(pathname: string): boolean {
  return FLOW_INSTANCE_PATH.test(pathname);
}

/**
 * Whether a location is in standalone mode: a flow-instance route carrying
 * `standalone=true`. Anywhere else (login, client list, public flow) is never
 * standalone, however the query looks.
 */
export function isStandaloneLocation(pathname: string, search: string): boolean {
  return (
    isFlowInstancePath(pathname) && new URLSearchParams(search).get(STANDALONE_PARAM) === "true"
  );
}

/**
 * Append the standalone params to an in-app path, MERGING with any query the
 * path already has (tab state, page selection, …) and preserving its hash.
 *
 * @param path An in-app path, optionally with `?query` and `#hash`.
 * @param internalRedirectUrl The raw link-carried return target to keep on the
 *   URL, or null/undefined when the link had none.
 */
export function mergeStandaloneParams(path: string, internalRedirectUrl?: string | null): string {
  const { pathname, search, hash } = splitPath(path);
  const params = new URLSearchParams(search);

  params.set(STANDALONE_PARAM, "true");
  if (internalRedirectUrl) params.set(INTERNAL_REDIRECT_URL_PARAM, internalRedirectUrl);
  else params.delete(INTERNAL_REDIRECT_URL_PARAM);

  return `${pathname}?${params.toString()}${hash}`;
}

/** Split an in-app path into its parts; `search` and `hash` keep their `?`/`#` prefix (or are ""). */
function splitPath(path: string): { pathname: string; search: string; hash: string } {
  const hashIndex = path.indexOf("#");
  const hash = hashIndex === -1 ? "" : path.slice(hashIndex);
  const withoutHash = hashIndex === -1 ? path : path.slice(0, hashIndex);

  const queryIndex = withoutHash.indexOf("?");
  const pathname = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
  const search = queryIndex === -1 ? "" : withoutHash.slice(queryIndex);
  return { pathname, search, hash };
}

/**
 * The only routes a standalone user may leave the flow for: the sign-out /
 * sign-in round trip (`/login`, `/login/verify-2fa`, `/clear`). Coming back
 * through them keeps the launch link in `?redirect=`.
 */
const STANDALONE_EXIT_PATH = /^\/(login|clear)(\/|$)/;

/** True for the sign-out / sign-in routes that standalone mode lets through. */
export function isStandaloneExitPath(pathname: string): boolean {
  return STANDALONE_EXIT_PATH.test(pathname);
}

/** Absolute URL with a scheme (`https:`, `mailto:`, …) or protocol-relative (`//host`). */
const ABSOLUTE_URL = /^([a-z][a-z0-9+.-]*:|\/\/)/i;

export type StandaloneNavigationTarget =
  /**
   * A flow-instance route. `to` is the target with the standalone params
   * guaranteed: identical to the input when it already carried them, else the
   * input with `standalone=true` (and the link's `internalRedirectUrl`) merged
   * back in — `corrected` tells the two apart.
   */
  | { kind: "in-flow"; to: string; corrected: boolean }
  /** The sign-out / sign-in round trip — allowed. */
  | { kind: "logout" }
  /** A URL on another origin or with a scheme — not part of QP, allowed. */
  | { kind: "external" }
  /** Any other part of QP (client list, client detail, settings, dashboard, …) — cancelled. */
  | { kind: "out-of-flow" };

/**
 * QL-262 — classify a navigation target while in standalone mode.
 *
 * @param target The navigation target: an absolute in-app path (`/clients/…`,
 *   optionally with `?query` and `#hash`) or an absolute URL. Relative paths
 *   cannot be resolved here and count as out-of-flow — react-router resolves
 *   them before they reach the guard, so none should arrive.
 * @param currentSearch The current location's `search`, so a corrected target
 *   keeps the link-carried `internalRedirectUrl` param.
 */
export function classifyStandaloneNavigation(
  target: string,
  currentSearch: string,
): StandaloneNavigationTarget {
  if (ABSOLUTE_URL.test(target)) return { kind: "external" };

  const { pathname, search } = splitPath(target);
  if (isStandaloneExitPath(pathname)) return { kind: "logout" };
  if (!isFlowInstancePath(pathname)) return { kind: "out-of-flow" };

  if (new URLSearchParams(search).get(STANDALONE_PARAM) === "true") {
    return { kind: "in-flow", to: target, corrected: false };
  }
  const paramRedirectUrl = new URLSearchParams(currentSearch).get(INTERNAL_REDIRECT_URL_PARAM);
  return { kind: "in-flow", to: mergeStandaloneParams(target, paramRedirectUrl), corrected: true };
}
